import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import { ApiVersion, ApiVersionDisplayText, MaxNumberOfV1ApiKeys } from '../constants';
import { messages } from './messages';
import style from './style.module.less';

/**
 * This component returns the selector to choose between V1 and V2 API Keys
 * @param {object} props
 * @param {Array<import('../../types/api-keys').APIKeysV1>} props.v1ApiKeys
 * @param {string} props.selectedApiVersion
 * @param {boolean} props.isCreatingApiKeys
 * @param {(fromBanner: boolean) => void} props.onGenerateButtonClick
 * @param {(selectedVersion: string) => void} props.onSelectedApiVersion
 * @returns {JSX.Element} The rendered component.
 */
export const ApiKeySelector = ({ v1ApiKeys, selectedApiVersion, isCreatingApiKeys, onGenerateButtonClick, onSelectedApiVersion }) => {
  const maxNumberOfApiKeysReach = v1ApiKeys?.length >= MaxNumberOfV1ApiKeys;
  const generateButtonDisabled = isCreatingApiKeys || maxNumberOfApiKeysReach;

  return (
    <div className={style['settings-api-section-header']}>
      <Text element='label' textType={TextType.Label} value={messages.selectApiVersion} />

      <div className={classnames(style['settings-api-selector-wrapper'])}>
        <div className={style['settings-api-selector']}>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth variant='form'>
            <DropdownMenuToggler theme='white'>
              {selectedApiVersion === ApiVersion.V1 ? ApiVersionDisplayText.V1 : ApiVersionDisplayText.V2}
            </DropdownMenuToggler>

            <DropdownMenuItem onClick={() => onSelectedApiVersion(ApiVersion.V1)} selected={selectedApiVersion === ApiVersion.V1}>
              {ApiVersionDisplayText.V1}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSelectedApiVersion(ApiVersion.V2)} selected={selectedApiVersion === ApiVersion.V2}>
              {ApiVersionDisplayText.V2}
            </DropdownMenuItem>
          </DropdownMenu>
        </div>

        {selectedApiVersion === ApiVersion.V1 && (
          <Tooltip shouldHideTooltip={!maxNumberOfApiKeysReach}>
            <span>
              <Button disabled={generateButtonDisabled} onClick={onGenerateButtonClick} theme='tertiary'>
                <FormattedMessage {...(isCreatingApiKeys ? messages.generatingV1Keys : messages.generateV1Keys)} />
              </Button>
            </span>

            <TooltipContent>
              <FormattedMessage
                {...messages.limitReachedToolTipContent}
                values={{
                  apiKeyLimit: MaxNumberOfV1ApiKeys,
                  keyCountToDelete: v1ApiKeys?.length - MaxNumberOfV1ApiKeys + 1,
                }}
              />
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
